export const AUTH_PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MIN_LENGTH = AUTH_PASSWORD_MIN_LENGTH;

export const AUTH_PASSWORD_POLICY_MESSAGE =
  `Use a password with at least ${AUTH_PASSWORD_MIN_LENGTH} characters that has not appeared in a public data breach.`;

export const AUTH_TEMPORARY_PASSWORD_POLICY_MESSAGE =
  `Use a temporary password with at least ${AUTH_PASSWORD_MIN_LENGTH} characters that has not appeared in a public data breach.`;

export const AUTH_WEAK_SIGN_IN_RESET_MESSAGE =
  `This password needs to be changed before you can continue. Use Forgot password to choose a new password with at least ${AUTH_PASSWORD_MIN_LENGTH} characters that has not appeared in a public data breach.`;

const PASSWORD_POLICY_LENGTH_MESSAGE = "Use at least 12 characters for your password.";
const PASSWORD_POLICY_COMPLEXITY_MESSAGE = "Use at least 3 of: uppercase, lowercase, number, symbol.";

export type AuthPasswordContext = "login" | "signup" | "reset" | "manualCreate";

export type WeakPasswordReason = "length" | "characters" | "pwned";

type MaybeRecord = Record<string, unknown>;

type WeakPasswordInfo = {
  message?: string;
  reasons?: WeakPasswordReason[];
};

function isRecord(value: unknown): value is MaybeRecord {
  return Boolean(value) && typeof value === "object";
}

function weakPasswordReasonsFrom(value: unknown): WeakPasswordReason[] | undefined {
  if (!Array.isArray(value)) return undefined;

  const reasons = value.filter(
    (reason): reason is WeakPasswordReason =>
      reason === "length" || reason === "characters" || reason === "pwned",
  );

  return reasons.length > 0 ? reasons : undefined;
}

function weakPasswordInfoFrom(value: unknown): WeakPasswordInfo | null {
  if (!isRecord(value)) return null;

  const reasons = weakPasswordReasonsFrom(value.reasons);
  const message = typeof value.message === "string" ? value.message : undefined;

  return { message, reasons };
}

export function validatePasswordPolicy(password: string): string | null {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return PASSWORD_POLICY_LENGTH_MESSAGE;
  }

  const characterClassCount = [
    /[A-Z]/.test(password),
    /[a-z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ].filter(Boolean).length;

  if (characterClassCount < 3) {
    return PASSWORD_POLICY_COMPLEXITY_MESSAGE;
  }

  return null;
}

export function isAuthWeakPasswordError(error: unknown): boolean {
  if (!isRecord(error)) return false;

  return (
    error.name === "AuthWeakPasswordError" ||
    error.code === "weak_password" ||
    error.error_code === "weak_password"
  );
}

export function getWeakPasswordFromSignInData(data: unknown): WeakPasswordInfo | null {
  if (!isRecord(data)) return null;

  const topLevelWeakPassword = weakPasswordInfoFrom(data.weakPassword);
  if (topLevelWeakPassword) return topLevelWeakPassword;

  const session = data.session;
  if (!isRecord(session)) return null;

  return weakPasswordInfoFrom(session.weak_password);
}

export function getAuthPasswordErrorMessage(
  error: unknown,
  context: AuthPasswordContext,
): string | null {
  if (!isAuthWeakPasswordError(error)) return null;

  switch (context) {
    case "login":
      return AUTH_WEAK_SIGN_IN_RESET_MESSAGE;
    case "manualCreate":
      return AUTH_TEMPORARY_PASSWORD_POLICY_MESSAGE;
    case "signup":
    case "reset":
      return AUTH_PASSWORD_POLICY_MESSAGE;
  }
}

export function getWeakPasswordSignInMessage(data: unknown): string | null {
  return getWeakPasswordFromSignInData(data) ? AUTH_WEAK_SIGN_IN_RESET_MESSAGE : null;
}
